/**
 * Event Reporting rates — effective-dated commission / override / demo pay.
 *
 * Everyone with event access sees the history. Editors (event_editor) add a
 * new row or delete one; rows are never edited in place, so a correction is a
 * new row with the right effective date. Deletes are soft: the report stops
 * using the row at once and the history keeps it (shown under "Show deleted").
 * A new rate changes Report 1's commission and demo pay immediately — the
 * view computes them at read time, no pipeline run needed.
 *
 * The event manager is part of each rate row (the person can change): she earns the
 * Manager override % on every event sale; on her own leads she also earns the regular
 * commission as promoter (2% + 1% = 3%). Changing manager = a new row with the new name.
 */
import { AlertCircle, Plus, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import type { FormEvent, ReactNode } from "react";

import ConfirmModal from "../../assumptions/ConfirmModal";
import { useAuth } from "../../auth/AuthContext";
import { EventsNav } from "../../events/EventsNav";
import { money, pct, todayYmd } from "../../events/format";
import { createEventConfig, deleteEventConfig, fetchEventConfig, fetchEventPromoters } from "../../lib/eventReporting";
import type { EventConfigRow } from "../../lib/eventReporting";

const inputCls = "h-9 px-3 py-1.5 rounded-md border hairline text-[13.5px] focus-ring w-full"; // fixed height: date/select/text align
const inputStyle = { background: "var(--bg)", color: "var(--ink)" };

/** "5" -> 0.05; "" -> null (not set). */
function pctToFraction(s: string): number | null {
  return s.trim() === "" ? null : Number(s) / 100;
}

export function EventConfig() {
  const { user } = useAuth();
  const canEdit = !!user?.has_event_editor;
  const [rows, setRows] = useState<EventConfigRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [effectiveFrom, setEffectiveFrom] = useState(todayYmd());
  const [commission, setCommission] = useState("");
  const [override, setOverride] = useState("");
  const [demoPay, setDemoPay] = useState("");
  const [manager, setManager] = useState("");
  const [promoters, setPromoters] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [showDeleted, setShowDeleted] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<EventConfigRow | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  function load() {
    fetchEventConfig().then(setRows).catch((e: Error) => setError(e.message));
  }
  useEffect(load, []);
  useEffect(() => {
    if (canEdit) fetchEventPromoters().then(setPromoters).catch(() => setPromoters([]));
  }, [canEdit]);

  // Prefill the form from the rate currently in effect, so an editor changes only what they mean to.
  const live = (rows ?? []).filter((r) => !r.deleted_at);
  const deletedCount = (rows?.length ?? 0) - live.length;
  const current = live.find((r) => r.effective_from <= todayYmd()) ?? null;
  useEffect(() => {
    if (!current) return;
    setCommission(current.commission_pct == null ? "" : String(+(current.commission_pct * 100).toFixed(4)));
    setOverride(current.override_pct == null ? "" : String(+(current.override_pct * 100).toFixed(4)));
    setDemoPay(String(current.demo_pay_rate));
    setManager(current.manager_promoter_name ?? "");
  }, [current?.id]); // re-prefill only when the row in effect changes

  async function submit(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    setSaved(null);
    const c = pctToFraction(commission), o = pctToFraction(override), d = Number(demoPay);
    for (const [label, v] of [["Commission", c], ["Manager override", o]] as const) {
      if (v !== null && (Number.isNaN(v) || v < 0 || v > 1)) return setFormError(`${label} must be between 0 and 100%.`);
    }
    if (demoPay.trim() === "" || Number.isNaN(d) || d < 0) return setFormError("Demo pay must be a dollar amount of 0 or more.");
    setSaving(true);
    try {
      const row = await createEventConfig({
        commission_pct: c, override_pct: o, demo_pay_rate: d, effective_from: effectiveFrom,
        manager_promoter_name: manager || null, manager_own_pct: null, // own-sales % retired 2026-10-21
      });
      setSaved(`Saved. Rates effective ${row.effective_from} now apply to the report.`);
      load();
    } catch (err) {
      setFormError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function confirmDelete() {
    const target = pendingDelete;
    setPendingDelete(null);
    if (!target) return;
    setDeleteError(null);
    try {
      await deleteEventConfig(target.id);
      load();
    } catch (err) {
      setDeleteError((err as Error).message);
    }
  }

  return (
    <div className="p-8 view-in overflow-y-auto h-full scroll-quiet">
      <header className="mb-4">
        <h1 className="font-serif text-[28px] leading-tight" style={{ color: "var(--ink)" }}>Event Rates</h1>
        <p className="text-[14.5px] font-serif italic mt-1" style={{ color: "var(--ink-3)" }}>
          Commission, event manager pay and demo pay used by the event reports.
        </p>
      </header>

      <EventsNav />

      {error && (
        <div className="rounded-md p-4 mb-4 font-mono text-[13px] flex items-start gap-2"
          style={{ background: "var(--oxblood-bg)", color: "var(--oxblood)", border: "1px solid var(--oxblood)" }}>
          <AlertCircle size={14} className="mt-0.5 shrink-0" />
          <span>Failed to load rates: {error}</span>
        </div>
      )}

      {canEdit && (
        <form onSubmit={submit} className="rounded-lg border hairline px-5 py-4 mb-5 max-w-6xl" style={{ background: "var(--bg-elev)" }}>
          <div className="font-serif text-[14px] mb-1" style={{ color: "var(--ink)" }}>Add a rate change</div>
          <p className="text-[12.5px] mb-3" style={{ color: "var(--ink-3)" }}>
            Applies to appointments on or after the effective date. Leave a percentage blank if it hasn't been decided.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            <Field label="Effective from">
              <input type="date" required value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} className={inputCls} style={inputStyle} />
            </Field>
            <Field label="Commission %">
              <input inputMode="decimal" placeholder="e.g. 5" value={commission} onChange={(e) => setCommission(e.target.value)} className={inputCls} style={inputStyle} />
            </Field>
            <Field label="Demo pay $">
              <input inputMode="decimal" required value={demoPay} onChange={(e) => setDemoPay(e.target.value)} className={inputCls} style={inputStyle} />
            </Field>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3 items-end">
            <Field label="Event manager">
              <select value={manager} onChange={(e) => setManager(e.target.value)} className={inputCls} style={inputStyle}>
                <option value="">No manager</option>
                {manager && !promoters.includes(manager) && <option value={manager}>{manager}</option>}
                {promoters.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </Field>
            <Field label="Manager override % (all event sales)">
              <input inputMode="decimal" placeholder="e.g. 1" value={override} onChange={(e) => setOverride(e.target.value)} className={inputCls} style={inputStyle} />
            </Field>
          </div>
          <p className="text-[12px] mt-2" style={{ color: "var(--ink-3)" }}>
            The manager earns the override on every event sale; on her own leads she also earns the regular commission
            as promoter. She's matched to leads by LeadPerfection promoter name. When the role changes hands, add a new
            row naming the new manager from the date they start; earlier events keep paying the previous manager.
          </p>
          <div className="flex items-center gap-3 mt-4">
            <button type="submit" disabled={saving}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md font-serif text-[14px] t-base focus-ring disabled:opacity-50"
              style={{ background: "var(--ink)", color: "var(--bg)" }}>
              <Plus size={14} strokeWidth={2} />
              {saving ? "Saving…" : "Save rate change"}
            </button>
            {formError && <span className="text-[13px]" style={{ color: "var(--oxblood)" }}>{formError}</span>}
            {saved && <span className="text-[13px]" style={{ color: "var(--forest)" }}>{saved}</span>}
          </div>
        </form>
      )}

      <section className="rounded-lg border hairline overflow-hidden max-w-6xl" style={{ background: "var(--bg-elev)" }}>
        <div className="flex items-center justify-between px-5 pt-4 pb-2">
          <span className="font-serif text-[14px]" style={{ color: "var(--ink)" }}>History</span>
          {deletedCount > 0 && (
            <label className="inline-flex items-center gap-2 text-[12.5px]" style={{ color: "var(--ink-3)" }}>
              <input type="checkbox" checked={showDeleted} onChange={(e) => setShowDeleted(e.target.checked)} />
              Show deleted ({deletedCount})
            </label>
          )}
        </div>
        {deleteError && (
          <div className="mx-5 mb-2 text-[13px]" style={{ color: "var(--oxblood)" }}>{deleteError}</div>
        )}
        {!rows && !error ? (
          <div className="p-5 font-serif italic text-[14px]" style={{ color: "var(--ink-3)" }}>Loading…</div>
        ) : (
          <table className="w-full text-left text-[13px]">
            <thead>
              <tr className="text-[10.5px] font-mono uppercase tracking-[0.14em] border-b hairline" style={{ color: "var(--ink-3)" }}>
                <th className="px-5 py-2 font-normal">Effective from</th>
                <th className="px-3 py-2 font-normal text-right">Commission</th>
                <th className="px-3 py-2 font-normal text-right">Demo pay</th>
                <th className="px-3 py-2 font-normal">Event manager</th>
                <th className="px-3 py-2 font-normal text-right">Override</th>
                <th className="px-5 py-2 font-normal">Entered</th>
                {canEdit && <th className="px-3 py-2 font-normal w-[1%]"><span className="sr-only">Actions</span></th>}
              </tr>
            </thead>
            <tbody>
              {(showDeleted ? rows ?? [] : live).map((r) => (
                <tr key={r.id} className="border-b hairline last:border-0"
                  style={{ color: r.deleted_at ? "var(--ink-4)" : "var(--ink)" }}>
                  <td className="px-5 py-2 font-mono">
                    {r.effective_from}
                    {r.id === current?.id && (
                      <span className="ml-2 text-[10.5px] uppercase tracking-[0.1em] px-1.5 py-0.5 rounded-sm"
                        style={{ background: "var(--forest-bg)", color: "var(--forest)" }}>In effect</span>
                    )}
                    {r.deleted_at && (
                      <span className="ml-2 text-[10.5px] uppercase tracking-[0.1em] px-1.5 py-0.5 rounded-sm"
                        style={{ background: "var(--oxblood-bg)", color: "var(--oxblood)" }}>
                        Deleted {new Date(r.deleted_at).toLocaleDateString("en-US")}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">{pct(r.commission_pct)}</td>
                  <td className="px-3 py-2 text-right font-mono">{money(r.demo_pay_rate)}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{r.manager_promoter_name ?? "–"}</td>
                  <td className="px-3 py-2 text-right font-mono">{pct(r.override_pct)}</td>
                  <td className="px-5 py-2 text-[12px]" style={{ color: "var(--ink-3)" }}>
                    {r.updated_at ? new Date(r.updated_at).toLocaleDateString("en-US") : ""}
                  </td>
                  {canEdit && (
                    <td className="px-3 py-2 text-right">
                      {!r.deleted_at && (
                        <button type="button" onClick={() => setPendingDelete(r)} disabled={live.length <= 1}
                          title={live.length <= 1 ? "The only rate can't be deleted. Add its replacement first." : "Delete this rate"}
                          aria-label={`Delete the rate effective ${r.effective_from}`}
                          className="p-1.5 rounded-md t-base focus-ring hover:bg-[var(--oxblood-bg)] disabled:opacity-30 disabled:hover:bg-transparent"
                          style={{ color: "var(--oxblood)" }}>
                          <Trash2 size={14} />
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <ConfirmModal
        isOpen={pendingDelete !== null}
        variant="danger"
        title="Delete this rate?"
        message={pendingDelete ? `The rate effective ${pendingDelete.effective_from} (commission ${pct(pendingDelete.commission_pct)}, override ${pct(pendingDelete.override_pct)}, demo pay ${money(pendingDelete.demo_pay_rate)}) stops applying to the report immediately. It stays in the history, marked deleted.` : ""}
        confirmLabel="Delete rate"
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <div className="text-[10.5px] font-mono uppercase tracking-[0.14em] mb-1" style={{ color: "var(--ink-3)" }}>{label}</div>
      {children}
    </label>
  );
}
